<template>
  <div class="container mx-auto p-4">
    <h2 class="text-2xl font-semibold mb-4">User List</h2>

    <!-- Search Input -->
    <div class="mb-4">
      <input
          v-model="searchQuery"
          type="text"
          placeholder="Search users..."
          class="w-full px-4 py-2 rounded border border-gray-700 bg-gray-800 text-white focus:ring-2 focus:ring-blue-500"
      />
    </div>

    <!-- Loading States -->
    <div v-if="isLoadingAll || isLoading" class="flex justify-center items-center">
      <svg
          class="animate-spin h-8 w-8 text-blue-500"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
      >
        <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
        ></circle>
        <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v8H4z"
        ></path>
      </svg>
      <span class="ml-2">{{ isLoadingAll ? 'Loading all users...' : 'Loading users...' }}</span>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="bg-red-100 text-red-700 p-3 rounded mb-4">
      {{ error }}
    </div>

    <!-- Users Table -->
    <div v-else>
      <div class="flex justify-between items-center mb-4">
        <button
            v-if="selectedUsers.size > 0"
            @click="handleSelectedUsers"
            class="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg shadow-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-150"
        >
          <span>Continue with {{ selectedUsers.size }} selected</span>
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd"
                  d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                  clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>
      <table class="min-w-full bg-gray-800 rounded-lg shadow-md divide-y divide-gray-700">
        <thead class="bg-gray-700">
        <tr>
          <th class="py-3 px-4 text-left">
            <input
                type="checkbox"
                :checked="selectedUsers.size === filteredUsers.length"
                :indeterminate="selectedUsers.size > 0 && selectedUsers.size < filteredUsers.length"
                @change="toggleAllUsers"
                class="w-4 h-4 rounded border-gray-600 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
            >
          </th>
          <th class="py-3 px-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Name</th>
          <th class="py-3 px-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Email</th>
        </tr>
        </thead>
        <tbody class="bg-gray-800 divide-y divide-gray-700">
        <tr
            v-for="user in paginatedUsers"
            :key="user.id"
            class="transition-colors duration-150 ease-in-out cursor-pointer"
            :class="{
              'bg-blue-700 hover:bg-gray-600': Array.from(selectedUsers).some(u => u.id === user.id),
              'hover:bg-gray-700': !Array.from(selectedUsers).some(u => u.id === user.id)
            }"
            @click="toggleUserSelection(user)"
        >
          <td class="py-3 px-4">
            <input
                type="checkbox"
                :checked="Array.from(selectedUsers).some(u => u.id === user.id)"
                @click.stop
                @change="toggleUserSelection(user)"
                class="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
            >
          </td>
          <td class="py-3 px-4 whitespace-nowrap">
            <div class="flex items-center space-x-4">
              <img
                  :src="user.images?.[0].imageUri || 'src/assets/userImage.webp'"
                  alt="User avatar"
                  class="h-10 w-10 rounded-full object-cover"
                  @error="handleImageError"
              />
              <span class="text-gray-300">{{ user.name }}</span>
            </div>
          </td>
          <td class="py-3 px-4 whitespace-nowrap text-gray-300">{{ user.email }}</td>
        </tr>
        </tbody>
      </table>
      <div class="flex justify-between items-center mt-4">
        <!-- Existing pagination controls -->
        <div class="flex items-center space-x-2">
          <label for="pageSize" class="text-gray-400 text-sm">Items per page:</label>
          <select
              id="pageSize"
              v-model="pageSize"
              class="bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option v-for="size in pageSizeOptions" :key="size" :value="size">
              {{ size }}
            </option>
          </select>
        </div>

        <div class="flex space-x-1">
          <button
              @click="currentPage--"
              :disabled="currentPage === 1"
              class="px-3 py-1.5 text-sm bg-gray-800 border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>

          <template v-for="(pageNum, index) in pageNumbers" :key="pageNum === '...' ? `ellipsis-${index}` : `page-${pageNum}`">
            <button
                v-if="pageNum !== '...'"
                @click="currentPage = Number(pageNum)"
                class="px-3 py-1.5 text-sm rounded-lg transition-colors"
                :class="pageNum === currentPage
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-800 border border-gray-700 text-gray-300 hover:bg-gray-700'"
                :disabled="pageNum === currentPage"
            >
              {{ pageNum }}
            </button>
            <span v-else class="px-3 py-1.5 text-sm text-gray-500">...</span>
          </template>

          <button
              @click="currentPage++"
              :disabled="currentPage === totalPages"
              class="px-3 py-1.5 text-sm bg-gray-800 border border-gray-700 text-gray-300 rounded-lg hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </div>
      </div>
    </div>
    <TransitionRoot appear :show="isMetricsModalOpen" as="template">
      <Dialog as="div" class="relative z-10" static >
        <!-- Overlay -->
        <TransitionChild
            enter="duration-300 ease-out"
            enter-from="opacity-0"
            enter-to="opacity-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100"
            leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </TransitionChild>

        <!-- Modal -->
        <div class="fixed inset-0 overflow-y-auto">
          <div class="flex min-h-full items-center justify-center p-4">
            <TransitionChild
                enter="duration-300 ease-out"
                enter-from="opacity-0 scale-95"
                enter-to="opacity-100 scale-100"
                leave="duration-200 ease-in"
                leave-from="opacity-100 scale-100"
                leave-to="opacity-0 scale-95"
            >
              <DialogPanel class="w-full max-w-4xl transform rounded-xl bg-gray-900 border border-gray-700 shadow-2xl transition-all">
                <div class="flex justify-between items-center px-6 py-4 border-b border-gray-700">
                  <div>
                    <DialogTitle class="text-base font-semibold text-white">
                      User Metrics
                    </DialogTitle>
                    <p class="text-sm text-gray-400 mt-0.5">{{ selectedUsers.size }} user{{ selectedUsers.size !== 1 ? 's' : '' }} selected</p>
                  </div>
                  <div class="flex gap-2">
                    <button
                        @click="handleCloseWithoutSaving"
                        class="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      Close
                    </button>
                    <button
                        @click="handleSaveAndClose"
                        class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Save
                    </button>
                  </div>
                </div>

                <div class="p-6">
                  <UserMetrics
                      ref="userMetricsRef"
                      :users="Array.from(selectedUsers)" />
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </div>
</template>

<script setup lang="ts">
import defaultUserImage from '@/assets/userImage.webp';
import {useUserStore} from '@/stores/userStore';
import {onMounted, computed, ref, watch, onUnmounted} from 'vue';
import {storeToRefs} from "pinia";
import UserMetrics from "@/components/UserMetrics.vue";
import type {User} from "@/types/User.ts";
import { Dialog, DialogPanel, DialogTitle, TransitionRoot, TransitionChild } from '@headlessui/vue';
import api from "@/services/api.ts";
import axios from "axios";

const userStore = useUserStore();

const {users, isLoading, error} = storeToRefs(userStore);

const emit = defineEmits(['users'])

const pageSizeOptions = [10, 25, 50, 100];
const selectedUsers = ref<Set<User>>(new Set());
const isButtonBouncing = ref(false);
const isLoadingAll = ref(false);
const searchQuery = ref('');
const currentPage = ref(1);
const pageSize = ref(10);
const userMetricsRef = ref<InstanceType<typeof UserMetrics> | null>(null);
const isMetricsModalOpen = ref(false);

// Fetch users when the component is mounted
onMounted(async () => {
  try {
    console.log('onMounted called');
    console.log('userStore:', userStore);
    console.log('userStore.fetchAllUsers:', userStore.fetchAllUsers);

    window.addEventListener('beforeunload', handleBeforeUnload);

    console.log('About to call userStore.fetchAllUsers()');
    const response = await userStore.fetchAllUsers();

    console.log('fetchAllUsers completed successfully:', response);
    users.value = response.users;
  } catch (err) {
    console.error('Error in onMounted:', err);
    console.error('Error details:', {
      message: err?.message,
      stack: err?.stack,
      name: err?.name
    });

    error.value = 'Failed to fetch users';
  } finally {
    console.log('onMounted finally block');
    isLoading.value = false;
  }
});

onUnmounted(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload)
})

const paginatedUsers = computed(() => {
  const start = (currentPage.value -1) * pageSize.value;
  const end = start + pageSize.value;
  return filteredUsers.value.slice(start, end);
})

// Computed property for limited page numbers with ellipses
const pageNumbers = computed(() => {
  const total = totalPages.value;
  const current = currentPage.value;
  const pages = [];

  if (total <= 5) {
    for (let i = 1; i <= total; i++) {
      pages.push(i);
    }
  } else {
    pages.push(1);

    if (current > 3) {
      pages.push('...');
    }

    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (current < total - 2) {
      pages.push('...');
    }

    pages.push(total);
  }

  return pages;
});

const totalPages = computed(() => Math.max(1, Math.ceil(filteredUsers.value.length / pageSize.value)));

const filteredUsers = computed(() => {
  const query = searchQuery.value.toLowerCase();
  return users.value.filter(user =>
      user.name.toLowerCase().includes(query) ||
      user.email.toLowerCase().includes(query)
  );
})

const toggleUserSelection = (user: User) => {
  const hasUser = Array.from(selectedUsers.value).some(u => u.id === user.id);
  if (hasUser) {
    selectedUsers.value = new Set(Array.from(selectedUsers.value).filter(u => u.id !== user.id));
  } else {
    selectedUsers.value.add(user);
  }
};

const toggleAllUsers = () => {
  if (selectedUsers.value.size === paginatedUsers.value.length) {
    selectedUsers.value.clear();
  } else {
    selectedUsers.value = new Set(paginatedUsers.value);
  }
};

const handleSelectedUsers = () => {
  isMetricsModalOpen.value = true;
  const selectedUsersList = Array.from(selectedUsers.value);
  emit('users', selectedUsersList);
};

const handleImageError = (event: Event) => {
  const target = event.target as HTMLImageElement;
  target.src = defaultUserImage;
  target.onerror = null;
};

const handleCloseWithoutSaving = () => {
    isMetricsModalOpen.value = false;
};

const handleBeforeUnload = (e: BeforeUnloadEvent) => {
  if (isMetricsModalOpen.value) {
    e.preventDefault();
    e.returnValue = '';
  }
}

const handleSaveAndClose = async () => {
  try {
    if (userMetricsRef.value) {
      const {metrics} = await (userMetricsRef.value as any).saveMetrics();

      const data = {
        items: Object.entries(metrics).flatMap(([userId, userMetrics]) =>
            userMetrics.map(metric => ({
              userId: metric.userId,
              metricId: metric.metricId,
              dateOccurred: new Date(String(metric.dateOccurred)).toLocaleDateString('en-CA', {
                year: 'numeric',
                month: '2-digit',
                day : '2-digit'
          }),
              value: Number(metric.value),
              count: Number(metric.count),
              type: 'Total'
            }))
        )
      };

      await api.post('/metrics', data);
      isMetricsModalOpen.value = false;
      selectedUsers.value.clear();
    }
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      console.error('API Error Response:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers,
      });
    } else {
      console.error('Unexpected error:', error);
    }
  }
}

watch([searchQuery, pageSize], () => {
  currentPage.value = 1;
});

watch(currentPage, (newPage) => {
    if (newPage < 1) {
      currentPage.value = 1;
    } else if (newPage > totalPages.value) {
      currentPage.value = newPage;
    }
}, { immediate: true });
</script>

<style scoped>
@keyframes double-bounce {
  0%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-6px);
  }
  60% {
    transform: translateY(0);
  }
  80% {
    transform: translateY(-4px);
  }
}

.bounce-attention {
  animation: double-bounce 1s cubic-bezier(0.36, 0, 0.66, -0.56);
}</style>
